import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { FC, memo, useCallback, useState } from "react";
import { MdStar, MdStarBorder } from "react-icons/md";
import { Link } from "react-router-dom";

import AppIcon from "@/components/Core/AppIcon";
import Image from "@/components/Core/Image";
import Translations from "@/components/Core/Translations";
import { routers } from "@/configs";
import { useGameState, useIsLoggedIn, useTranslate } from "@/hooks";
import { Game } from "@/types";

import { GAME_GRID_ITEM_SIZE } from "../data";
import {
  CardChips,
  CardWrapper,
  FavoriteButton,
  GameNameText,
  RtpChip,
  TitleOverlayContent,
} from "./styles";

// In the player's own format ("10 Oct 2026"), not the ISO date it was.
const formatAdded = (iso?: string): string => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return "";
  }
};

const GameCardComponent: FC<Partial<Game>> = (props) => {
  const { gid = "", name, image, count, addedAt, route, rtp, bonusBuy } = props;

  const t = useTranslate();
  const isLoggedIn = useIsLoggedIn();
  const { isFavorite, toggleFavoriteWithPersist, addRecent, cacheGame } = useGameState();
  const favorite = isFavorite(gid);

  /** True once the player has pressed the star on this card. */
  const [touched, setTouched] = useState(false);

  const handleToggleFavorite = useCallback(() => {
    setTouched(true);
    toggleFavoriteWithPersist(gid, props as Game);
  }, [gid, props, toggleFavoriteWithPersist]);

  // One of our own games is played on its own page, so go there: the game
  // room would only frame the site inside itself, behind a Play button that
  // has nothing to launch. A provider game still goes through the room,
  // which is where its launch URL is fetched.
  const path = route || routers.GameRoom.replace(":gameId", gid);

  const handleCardClick = useCallback(() => {
    if (gid) cacheGame(props as Game);
    addRecent(gid);
  }, [gid, props, cacheGame, addRecent]);

  return (
    <CardWrapper size={GAME_GRID_ITEM_SIZE}>
      {(typeof rtp === "number" || bonusBuy) && (
        <CardChips>
          {typeof rtp === "number" && (
            <RtpChip aria-label={t("game.card.rtp-aria", { rtp })}>
              {t("game.card.rtp", { rtp })}
            </RtpChip>
          )}
          {bonusBuy && <RtpChip>{t("game.list.bonus-buy")}</RtpChip>}
        </CardChips>
      )}

      {/* Favorites belong to an account, so a visitor is not offered the star. */}
      {isLoggedIn && (
        <FavoriteButton
          className="game-card-favorite"
          active={favorite}
          pop={touched && favorite}
          aria-label={name ? `${t("game.card.favorite")}: ${name}` : t("game.card.favorite")}
          aria-pressed={favorite}
          onClick={handleToggleFavorite}
        >
          {favorite ? <MdStar /> : <MdStarBorder />}
        </FavoriteButton>
      )}

      {/* A real link: the keyboard reaches it, and a middle click opens the
          game in a new tab. It was a div with a click handler. */}
      <Stack
        className="game-card"
        component={Link}
        to={path}
        onClick={handleCardClick}
        aria-label={t("game.card.aria-label", { name })}
      >
        <Image
          src={image}
          alt={name || t("game.card.image-alt")}
          width="100%"
          ratio="3/4"
          loading="lazy"
        />
        <Box className="game-card-title">
          <TitleOverlayContent>
            <GameNameText component="p" className="ellipsis-2-lines">
              <Translations text={name || ""} />
            </GameNameText>
            {!!count && (
              <Stack direction="row" alignItems="center" justifyContent="center" gap={1} mt={1}>
                <AppIcon name="dot" color="greenBhabua" size={7} />
                <Typography variant="caption">{t("game.card.online", { count })}</Typography>
              </Stack>
            )}
            {addedAt && (
              <Typography
                variant="caption"
                sx={{ display: "block", mt: count ? 0.5 : 1, opacity: 0.7 }}
              >
                {t("game.card.added", { date: formatAdded(addedAt) })}
              </Typography>
            )}
          </TitleOverlayContent>
        </Box>
      </Stack>
    </CardWrapper>
  );
};

export const GameCard = memo(GameCardComponent);
